import paymentApi from '../../utils/paymentApi';

export const UNIONPAY_B2B_CODE = 'unionpay_b2b';

const LOCAL_PAYMENT_TEST_HOSTS = new Set(['localhost', '127.0.0.1', '::1', '[::1]']);

export function isPrototypePaymentTestEnabled(locationValue = typeof window !== 'undefined' ? window.location : null) {
  if (!locationValue) return false;
  const hostname = String(locationValue.hostname || '').trim().toLowerCase();
  if (!LOCAL_PAYMENT_TEST_HOSTS.has(hostname)) return false;
  return new URLSearchParams(String(locationValue.search || '')).get('payment') === UNIONPAY_B2B_CODE;
}

const UNIONPAY_SAFE_REQUIREMENTS = [
  '银联商户与产品权限',
  '商户签名证书及银联验签证书链',
  '企业收款账户资料',
  '公网 HTTPS 支付回调与返回地址',
  '点数包和订阅对应的业务场景',
];

export const DEFAULT_PAYMENT_CHANNELS = [
  {
    code: 'wechat',
    label: '微信支付',
    visible: true,
    enabled: true,
    hint: '使用微信扫码付款',
  },
  {
    code: 'alipay',
    label: '支付宝',
    visible: true,
    enabled: false,
    hint: '跳转支付宝收银台付款',
  },
  {
    code: UNIONPAY_B2B_CODE,
    label: '企业网银（对公支付）',
    visible: false,
    enabled: false,
    hint: '测试入口已开放，完成商户证书、收款账户和公网回调配置后可发起',
    requirements: UNIONPAY_SAFE_REQUIREMENTS,
  },
];

export async function getPaymentChannels() {
  const response = await paymentApi.get('/payment/channels');
  if (Number(response?.data?.code) !== 200) {
    throw new Error(response?.data?.message || 'PAYMENT_CHANNELS_UNAVAILABLE');
  }
  const rows = Array.isArray(response?.data?.data?.list) ? response.data.data.list : [];
  const byCode = new Map(rows.map(item => [String(item?.code || ''), item]));

  return DEFAULT_PAYMENT_CHANNELS.map(fallback => {
    const remote = byCode.get(fallback.code);
    if (!remote) return fallback;
    return {
      ...fallback,
      label: String(remote.label || fallback.label),
      hint: fallback.code === 'alipay' ? fallback.hint : String(remote.hint || fallback.hint),
      // 企业网银暂不在公共结算页展示；本机显式沙箱入口仍可用于联调。
      visible: fallback.code === UNIONPAY_B2B_CODE ? false : Boolean(remote.visible),
      enabled: Boolean(remote.enabled),
    };
  });
}

export function paymentPrefix(method) {
  if (method === 'wechat') return '/wechat-pay';
  if (method === 'alipay') return '/alipay';
  if (method === UNIONPAY_B2B_CODE) return '/unionpay-b2b';
  return '';
}

export function paymentMethodFromChannel(channel) {
  const value = String(channel || '');
  if (value.startsWith('wechat')) return 'wechat';
  if (value.startsWith('alipay')) return 'alipay';
  if (value === UNIONPAY_B2B_CODE) return UNIONPAY_B2B_CODE;
  return '';
}

export const opensCashierWindow = method => method === 'alipay' || method === UNIONPAY_B2B_CODE;

// 支付宝以真实下单接口作为最终可用性判断，避免渠道状态请求失败或缓存未刷新时
// 把正式电脑网站支付拦截在浏览器端；服务端仍会在创建订单前执行完整配置校验。
export function canRequestPayment(channel) {
  return channel?.code === 'alipay' || Boolean(channel?.enabled);
}

export function paymentMethodLabel(method, english = false) {
  if (method === 'wechat') return english ? 'WeChat Pay' : '微信支付';
  if (method === 'alipay') return english ? 'Alipay' : '支付宝';
  if (method === UNIONPAY_B2B_CODE) return english ? 'Business banking' : '企业网银（对公支付）';
  return english ? 'Payment' : '支付';
}

export function paymentMethodHint(channel, english = false) {
  if (!channel) return '请选择可用支付方式';
  if (channel.code === 'alipay') return english ? 'Open the Alipay cashier' : '跳转支付宝收银台付款';
  if (channel.code === UNIONPAY_B2B_CODE) return channel.enabled
    ? (english ? 'Open the test cashier' : '进入测试收银台')
    : ('企业网银暂不可用');
  if (!english) return channel.hint;
  if (!channel.enabled) {
    return '该支付方式暂不可用';
  }
  if (channel.code === 'wechat') return 'Scan a QR code in WeChat';
  return 'Secure online payment';
}

export async function getSandboxPaymentChannels() {
  const response = await paymentApi.get('/unionpay-b2b/sandbox/channel');
  if (Number(response?.data?.code) !== 200) {
    throw new Error(response?.data?.message || 'SANDBOX_PAYMENT_CHANNEL_UNAVAILABLE');
  }
  const payload = response?.data?.data;
  const rows = Array.isArray(payload?.list)
    ? payload.list
    : payload?.channel
      ? [payload.channel]
      : payload && typeof payload === 'object'
        ? [payload]
        : [];
  const remote = rows.find(item => String(item?.code || UNIONPAY_B2B_CODE) === UNIONPAY_B2B_CODE);
  const fallback = DEFAULT_PAYMENT_CHANNELS.find(channel => channel.code === UNIONPAY_B2B_CODE);
  if (!remote) return [fallback];
  return [{
    ...fallback,
    label: String(remote.label || fallback.label),
    hint: String(remote.hint || fallback.hint),
    visible: true,
    enabled: Boolean(remote.enabled),
  }];
}

export function paymentCreateEndpoint(method) {
  const prefix = paymentPrefix(method);
  return prefix ? `${prefix}/pay` : '';
}

export function paymentChannelRequirements(channel, english = false) {
  if (channel?.code !== UNIONPAY_B2B_CODE || channel.enabled) return [];
  if (english) {
    return [
      'UnionPay merchant and product access',
      'Merchant signing certificate and UnionPay trust chain',
      'Corporate payee account details',
      'Public HTTPS callback and return URLs',
      'Registered business scenes for credits and subscriptions',
    ];
  }
  return Array.isArray(channel.requirements) && channel.requirements.length
    ? channel.requirements
    : UNIONPAY_SAFE_REQUIREMENTS;
}
